<template>
  <div class="modal-dim on">
    <!--'on 추가'-->
    <div class="modal-wrap">
      <div class="modal-inner whole">
        <div class="modal">
          <header class="header">
            <div class="inner">
              <h1 class="ttl">비즈니스 검색</h1>

              <div class="right" style="padding-left: 5px">
                <button class="header-btn" @click="close"><i class="ico-close-gray90"></i></button>
              </div>
            </div>
          </header>
          <!-- head fixed -->
          <div class="head">
            <div class="search-inner">
              <label class="label search">
                <input
                  type="text"
                  placeholder="비즈니스명, 카테고리, 태그, 상세주소"
                  @input="searchPreview($event)"
                  @compositionstart="isComposing = true"
                  @compositionend="onCompositionEnd"
                  @keydown.enter.prevent="onEnter"
                  v-model="textRef"
                  inputmode="search"
                />
                <button class="remove" @click="textRemove()"><i class="ico-button-remove"></i></button>
              </label>
            </div>
          </div>
          <!-- // head fixed -->
          <div class="search-list-wrap">
            <div class="title-wrap" v-if="!searchFlag">
              <p>최근 검색</p>
              <button @click="deleteRecent(-1, true)" v-if="!isEmpty(recentSearch)">전체 삭제</button>
            </div>
            <empty-items v-if="!searchFlag && isEmpty(recentSearch)" content="검색 내역이 없습니다." />
            <ul v-if="!searchFlag">
              <li v-for="(item, idx) in recentSearch" @click="search(item)">
                <div class="address-title">
                  <h6 class="title">{{ item }}</h6>
                </div>
                <button name="test" id="test" @click.prevent.stop="deleteRecent(idx)"><i class="ico-close"></i></button>
              </li>
            </ul>

            <empty-items v-if="searchFlag && isEmpty(businessPreviewList)" content="검색 내역이 없습니다." />
            <ul v-if="searchFlag && !isEmpty(businessPreviewList)">
              <li v-for="preview in businessPreviewList" @click="search(preview.name, true)">
                <div class="address-title">
                  <h6 class="title">
                    <i class="ico-fill-pin"></i>
                    <p v-html="highlightKeyword(preview.name, textRef)"></p>
                  </h6>
                  <div class="address-wrap">
                    <p
                      class="address"
                      v-html="highlightAddressKeyword(preview.address, preview.addressDetail, textRef)"
                    ></p>
                    <p class="distance">
                      {{ formatDistance(preview.distance) }}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, onMounted, ref } from 'vue';
import store from '@/store';
import { throttle } from 'lodash';
import EmptyItems from '@/components/common/layouts/EmptyItems.vue';
import { formatDistance, getCurrentLocation, isEmpty } from '@/common/utils/Util';
import { BOTTOM_SHEET } from '@/common/constants/ConstCode';
import { showTextModal } from '@/common/utils/ModalState';
const props = defineProps(['data']);
const emits = defineEmits(['close']);
const { fnCallBack, container } = props.data;
const businessPreviewList = computed(() => store.getters['business/getBusinessPreviewList']);
const businessSearchList = computed(() => store.getters['business/getBusinessSearchList']);
const recentSearch = computed(() => store.getters['business/getRecentSearch']);
const searchText = computed(() => store.getters['business/getSearchText']);
const user = computed(() => store.getters['user/getUserData']);

const textRef = ref('');
const searchFlag = ref(false);
const isComposing = ref(false);

const onCompositionEnd = event => {
  isComposing.value = false;
  textRef.value = event.target.value;
};

const onEnter = event => {
  if (isComposing.value || event.isComposing) return;
  search(event.target.value);
};

onMounted(() => {
  textRef.value = searchText.value;
});

const searchPreview = throttle(async event => {
  const value = event.target.value;
  if (isEmpty(value)) {
    store.commit('business/businessPreviewList', []);
    return;
  }
  searchFlag.value = true;
  textRef.value = value;
  store.dispatch('business/getSearchPreview', value);
}, 300);

const search = async (text, isListClick = false) => {
  if (!isListClick && !text) {
    showTextModal('검색어를 입력해주세요.', '확인');
    return false;
  }

  store.commit('business/initBusinessList');
  // 260512: 이전 카테고리/구인/이벤트/기부 필터가 condition에 누적되면 (Object.assign merge)
  // 재검색 시 BE에 같이 전달되어 0건 응답. searchText만 남기고 깨끗이 초기화.
  store.commit('business/condition', {
    searchText: '',
    userNo: '',
    page: 1,
    limit: 10,
    businessCategoryNo: '',
    isJob: false,
    isDonation: false,
    isEvent: false
  });
  store.commit('business/searchText', text);
  store.commit('layout/setBottomSheet', BOTTOM_SHEET.BUSINESS_SEARCH);
  store.commit('business/setCategorySearchFlag');
  await store.dispatch('business/getBusinessList', { isKakao: true, isOneKM: true, isSearch: true });
  if (!isEmpty(businessSearchList.value)) {
    const { latitude, longitude } = businessSearchList.value[0];
    if (fnCallBack) fnCallBack(latitude, longitude);
  }

  // 최근 검색어 순서 정렬
  const search = [...recentSearch.value];
  const index = search.indexOf(text);
  if (index !== -1) {
    search.splice(index, 1); // 기존 위치에서 제거
  }
  search.unshift(text); // 맨 앞에 추가
  textRef.value = text;
  store.commit('business/recentSearch', search);

  close();
};

const deleteRecent = (idx, init = false) => {
  let search = [...recentSearch.value];
  if (init) {
    search = [];
  } else {
    search.splice(idx, 1); // 기존 위치에서 제거
  }
  store.commit('business/recentSearch', search);
};

const highlightKeyword = (text, keyword) => {
  if (!keyword) return text;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // 정규식 특수문자 escape
  const regex = new RegExp(`(${escaped})`, 'gi');
  return text.replace(regex, '<span class="color-primary-dark">$1</span>');
};

const highlightAddressKeyword = (address, addressDetail, keyword) => {
  if (!keyword) return address;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // 정규식 특수문자 escape
  const regex = new RegExp(`(${escaped})`, 'gi');

  let text = `${address}${addressDetail ? `(${addressDetail})` : ''}`;
  return text.replace(regex, '<span class="color-primary-dark">$1</span>');
};

const textRemove = () => {
  textRef.value = '';
  searchFlag.value = false;
  store.commit('business/searchText');
};

const close = () => {
  setTimeout(() => {
    store.commit('layout/setIsNav', true);
    emits('close');
  }, 200);
};
</script>

<style scoped></style>
